// client/src/components/pages/Settings/LexiStarMaintenanceCodes/MaintenanceCodes.tsx (ST-00249)
//
// Settings → LexiStar → Maintenance & delay codes, /settings/modules/lexistar/maintenance-codes.
// Page permission lexistar.maintenance.view or .manage (config/pagePermissions); changing anything
// needs .manage (in-page). The organisation's codes, grouped by category — Planned maintenance,
// Breakdown, Delay — each with its label, its category (a change applies to NEW events only:
// booked ones keep the category they were booked with), active switch and place in the list (up /
// down). "Add a code". Codes are never deleted (switch one off); the first visit creates the
// default set (the server seeds it). A global admin sees every organisation's codes and picks
// the organisation first. Server: /api/lexistar/maintenance/codes.

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { FaArrowDown, FaArrowUp, FaListUl, FaPlus } from 'react-icons/fa';
import { ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { createCode, errorText, listCodes, reorderCodes, updateCode } from '../../../../services/lexistarMaintenance';
import { listTenants } from '../../../../services/lexistarBilling';
import type { EventCategory, MaintenanceCode } from '../../../../types/lexistar-maintenance';
import { ErrorBanner, LoadingState, NoPermissionPage, NoticeBanner, PageHeader, cardClasses, inputClasses } from '../../LexiStar/StatusBoard/atoms';
import { CATEGORY_META, EVENT_CATEGORY_ORDER } from '../../LexiStar/Maintenance/maintenanceLogic';

const VIEW = 'lexistar.maintenance.view';
const MANAGE = 'lexistar.maintenance.manage';

const CATEGORY_OPTIONS: Array<{ value: EventCategory; label: string }> = [
  { value: 'planned_maintenance', label: 'Planned maintenance' },
  { value: 'breakdown', label: 'Breakdown' },
  { value: 'delay', label: 'Delay' },
];

/** The codes in display order, per category (planned maintenance, breakdown, delay). */
export function groupCodes(codes: readonly MaintenanceCode[]): Array<{ category: EventCategory; codes: MaintenanceCode[] }> {
  const order: EventCategory[] = ['planned_maintenance', 'breakdown', 'delay'];
  return order.map((category) => ({
    category,
    codes: codes.filter((c) => c.category === category).sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label)),
  }));
}

/** The whole list with `id` moved one place up (-1) or down (+1) within its category. */
export function moveCode(codes: readonly MaintenanceCode[], id: string, dir: -1 | 1): string[] {
  const groups = groupCodes(codes);
  const ids: string[] = [];
  for (const g of groups) {
    const list = g.codes.map((c) => c.id);
    const i = list.indexOf(id);
    if (i !== -1) {
      const j = i + dir;
      if (j >= 0 && j < list.length) [list[i], list[j]] = [list[j]!, list[i]!];
    }
    ids.push(...list);
  }
  return ids;
}

function CodeRow({
  code,
  canManage,
  first,
  last,
  busy,
  onSave,
  onMove,
}: {
  code: MaintenanceCode;
  canManage: boolean;
  first: boolean;
  last: boolean;
  busy: boolean;
  onSave: (patch: { label?: string; category?: EventCategory; active?: boolean }) => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const [label, setLabel] = useState(code.label);
  useEffect(() => setLabel(code.label), [code.label]);
  const dirty = label.trim() !== code.label && label.trim().length > 0;
  const idBase = `code-${code.id}`;
  return (
    <li className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 ${code.active ? '' : 'bg-slate-50'}`} data-code={code.code}>
      <div className="flex items-center gap-1" role="group" aria-label={`Move ${code.label}`}>
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={!canManage || first || busy}
          aria-label={`Move ${code.label} up`}
          className="touch-target p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30"
        >
          <FaArrowUp size={11} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={!canManage || last || busy}
          aria-label={`Move ${code.label} down`}
          className="touch-target p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30"
        >
          <FaArrowDown size={11} aria-hidden="true" />
        </button>
      </div>
      <span className="font-mono text-xs font-semibold text-slate-600 w-36 truncate" title="The code never changes">
        {code.code}
      </span>
      <div className="flex-1 min-w-[12rem]">
        <label htmlFor={`${idBase}-label`} className="sr-only">
          Label of {code.code}
        </label>
        <input
          id={`${idBase}-label`}
          value={label}
          maxLength={80}
          disabled={!canManage || busy}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && dirty) onSave({ label: label.trim() });
          }}
          className={`${inputClasses} ${code.active ? '' : 'text-slate-500'}`}
        />
      </div>
      {dirty && (
        <button type="button" onClick={() => onSave({ label: label.trim() })} disabled={busy} className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg">
          Save
        </button>
      )}
      <div className="w-52">
        <label htmlFor={`${idBase}-category`} className="sr-only">
          Category of {code.code}
        </label>
        <ThemedSelect
          id={`${idBase}-category`}
          value={code.category}
          disabled={!canManage || busy}
          onChange={(e) => onSave({ category: String(e.target.value) as EventCategory })}
          aria-label={`Category of ${code.label}`}
        >
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </ThemedSelect>
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700 w-24">
        <input
          type="checkbox"
          checked={code.active}
          disabled={!canManage || busy}
          onChange={(e) => onSave({ active: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300"
        />
        {code.active ? 'Active' : 'Off'}
      </label>
      <span className="text-xs text-slate-500 w-20 text-right tabular-nums" title="Events booked with this code (cancelled ones not counted)">
        {code.events ?? 0} event{code.events === 1 ? '' : 's'}
      </span>
    </li>
  );
}

export default function MaintenanceCodes() {
  const { hasPermission, tenant } = useAuth();
  const canView = hasPermission(VIEW) || hasPermission(MANAGE);
  const canManage = hasPermission(MANAGE);
  const [codes, setCodes] = useState<MaintenanceCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState<EventCategory>('planned_maintenance');
  const [org, setOrg] = useState<number | null>(null);
  const [orgNames, setOrgNames] = useState<Map<number, string>>(new Map());

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      const res = await listCodes();
      setCodes(res.codes);
      if (res.seeded) setNotice(`The ${res.seeded} standard codes were set up for your organisation. Change them to suit you.`);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load the codes.'));
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  // A global admin gets every organisation's codes: one organisation at a time.
  const orgs = useMemo(() => [...new Set(codes.map((c) => c.tenant_id))].sort((a, b) => a - b), [codes]);
  const many = orgs.length > 1;
  // The organisation shown: the one picked, else the user's own, else the first (no flash of
  // every organisation's codes mixed together before a pick).
  const own = tenant?.tenant_id != null ? Number(tenant.tenant_id) : null;
  const currentOrg: number | null = !many
    ? null
    : org !== null && orgs.includes(org)
      ? org
      : own !== null && orgs.includes(own)
        ? own
        : (orgs[0] ?? null);
  useEffect(() => {
    if (!many) return;
    listTenants()
      .then((list) => setOrgNames(new Map(list.map((t) => [t.tenant_id, t.tenant_name]))))
      .catch(() => undefined);
  }, [many]);
  const shown = useMemo(() => (many ? codes.filter((c) => c.tenant_id === currentOrg) : codes), [codes, many, currentOrg]);
  const groups = useMemo(() => groupCodes(shown), [shown]);

  const save = async (code: MaintenanceCode, patch: { label?: string; category?: EventCategory; active?: boolean }) => {
    setBusy(true);
    try {
      const out = await updateCode(code.id, { ...patch, version: code.version });
      setCodes((prev) => prev.map((c) => (c.id === out.id ? { ...out, events: c.events } : c)));
      setNotice(
        patch.category
          ? `${out.label} is now ${CATEGORY_META[out.category].label.toLowerCase()} for new events. Events already booked keep their category.`
          : patch.active !== undefined
            ? `${out.label} is ${out.active ? 'switched on' : 'switched off: it is no longer offered for new events'}.`
            : `Saved: ${out.label}.`
      );
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not save the code.'));
      load();
    } finally {
      setBusy(false);
    }
  };

  const move = async (id: string, dir: -1 | 1) => {
    setBusy(true);
    try {
      const res = await reorderCodes(moveCode(shown, id, dir), currentOrg);
      setCodes((prev) => [...prev.filter((c) => !res.codes.some((r) => r.id === c.id)), ...res.codes]);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not change the order.'));
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!newCode.trim() || !newLabel.trim()) {
      setError('Give the new code a short code and a label.');
      return;
    }
    setBusy(true);
    try {
      const out = await createCode({
        code: newCode.trim(),
        label: newLabel.trim(),
        category: newCategory,
        ...(currentOrg !== null ? { tenant_id: currentOrg } : {}),
      });
      setCodes((prev) => [...prev, { ...out, events: 0 }]);
      setNotice(`Added ${out.label} (${CATEGORY_META[out.category].label.toLowerCase()}).`);
      setNewCode('');
      setNewLabel('');
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not add the code.'));
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <PageHeader
      icon={FaListUl}
      title="Maintenance & delay codes"
      subtitle="What can be booked against a truck's time, and whether it counts as planned maintenance, a breakdown or a delay."
    />
  );

  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view the maintenance codes." />;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6 space-y-5">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
        <p className="text-sm text-slate-600 max-w-3xl">
          A truck's time is <strong>available</strong> unless an event says otherwise. Each code belongs to one category. If you move a code to
          another category, only events booked from then on use the new one: what was booked keeps its category, so past figures do not change.
          Switch a code off to stop offering it; it is never deleted.
        </p>
        {many && (
          <div className="max-w-xs">
            <label htmlFor="codes-org" className="block text-xs font-medium text-slate-600 mb-1">
              Organisation
            </label>
            <ThemedSelect id="codes-org" value={currentOrg === null ? '' : String(currentOrg)} onChange={(e) => setOrg(Number(e.target.value))} aria-label="Organisation">
              {orgs.map((t) => (
                <option key={t} value={String(t)}>
                  {orgNames.get(t) || `Organisation ${t}`}
                </option>
              ))}
            </ThemedSelect>
          </div>
        )}
        {loading ? (
          <LoadingState label="Loading the codes…" />
        ) : (
          groups.map((g) => {
            const meta = CATEGORY_META[g.category];
            return (
              <section key={g.category} className={`${cardClasses} overflow-hidden`} aria-labelledby={`codes-${g.category}`}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${meta.pill}`} aria-hidden="true">
                    <meta.Icon size={13} />
                  </span>
                  <h2 id={`codes-${g.category}`} className="text-sm font-semibold text-slate-900">
                    {meta.label}
                  </h2>
                  <span className="text-xs text-slate-500">· {meta.hint}</span>
                </div>
                {g.codes.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-500">No codes yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {g.codes.map((c, i) => (
                      <CodeRow
                        key={c.id}
                        code={c}
                        canManage={canManage}
                        first={i === 0}
                        last={i === g.codes.length - 1}
                        busy={busy}
                        onSave={(patch) => save(c, patch)}
                        onMove={(dir) => move(c.id, dir)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })
        )}
        {canManage && !loading && (
          <form onSubmit={add} className={`${cardClasses} p-4 space-y-3`} aria-labelledby="codes-add-title">
            <h2 id="codes-add-title" className="text-sm font-semibold text-slate-900">
              Add a code
            </h2>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <label htmlFor="code-new-code" className="block text-xs font-medium text-slate-600 mb-1">
                  Code
                </label>
                <input
                  id="code-new-code"
                  value={newCode}
                  maxLength={32}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                  placeholder="GEARBOX"
                  className={`${inputClasses} font-mono`}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="flex-1 min-w-[12rem]">
                <label htmlFor="code-new-label" className="block text-xs font-medium text-slate-600 mb-1">
                  Label
                </label>
                <input id="code-new-label" value={newLabel} maxLength={80} onChange={(e) => setNewLabel(e.target.value)} placeholder="Gearbox failure" className={inputClasses} />
              </div>
              <div className="w-52">
                <label htmlFor="code-new-category" className="block text-xs font-medium text-slate-600 mb-1">
                  Category
                </label>
                <ThemedSelect id="code-new-category" value={newCategory} onChange={(e) => setNewCategory(String(e.target.value) as EventCategory)} aria-label="Category">
                  {EVENT_CATEGORY_ORDER.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_META[c].label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
              <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-60">
                <FaPlus size={11} aria-hidden="true" /> Add code
              </button>
            </div>
            <p className="text-xs text-slate-500">The code is A–Z, 0–9, _ and -, and never changes once added. The label is what people choose from.</p>
          </form>
        )}
      </div>
    </div>
  );
}
